import PropTypes from "prop-types";
import { connect } from "react-redux";
import React, { useState, useRef, useEffect } from "react";
import responseHandler from "responseHandler";
import { MSpinner, MDialog } from "components/Shared";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { getCubiCasaSketch } from "requests/homeowner/caseforms";
import SketchImageWrapper from "./ReadOnly/SketchImageWrapper";
import { submitFixRequest } from "requests/cubiCasa/index.ts";
import { CUBICASA_FIX_REQUEST, CUBICASA_QUICKEDIT, CUBICASA_ADVANCEDEDIT } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";

const CubiCasaSketch = ({
  controlNumber,
  makeErrorToast,
  makeSuccessToast,
  canFixRequest,
  canQuickEdit,
  canAdvancedEdit,
}) => {
  const mounted = useRef(true);
  const [cubiCasa, setCubiCasa] = useState(null);
  const [quickEdit, setQuickEdit] = useState(null);
  const [advancedEdit, setAdvancedEdit] = useState(null);
  const [showFixRequest, setShowFixRequest] = useState(false);
  const [fixRequestText, setFixRequestText] = useState("");
  const [submittingFixRequest, setSubmittingFixRequest] = useState(false);
  const [fetchingCubiCasa, setFetchingCubiCasa] = useState(false);

  useEffect(() => {
    setFetchingCubiCasa(true);
    getCubiCasaSketch(
      controlNumber,
      (data) => {
        if (data.sketchComp) {
          setCubiCasa(
            data.sketchComp.sketchImages.map((item, ix) => {
              return (
                <Grid key={ix} size={4}>
                  <SketchImageWrapper src={item} number={ix + 1} />
                </Grid>
              );
            }),
          );
        }
        setQuickEdit(data.quickEditLink);
        setAdvancedEdit(data.advancedEditLink);
        setFetchingCubiCasa(false);
      },
      () => {
        setFetchingCubiCasa(false);
      },
      mounted,
    );
  }, []);

  const submit = () => {
    if (!fixRequestText) {
      makeErrorToast("Please provide a description for the fix request");
    } else {
      setSubmittingFixRequest(true);
      submitFixRequest(
        controlNumber,
        {
          text: fixRequestText,
          images: [],
        },
        () => {
          setFixRequestText("");
          setShowFixRequest(false);
          setSubmittingFixRequest(false);
          makeSuccessToast("Fix Request Successfully Submitted");
        },
        () => {
          setSubmittingFixRequest(false);
          makeErrorToast("Error Submitting Fix Request");
        },
        mounted,
      );
    }
  };

  if (fetchingCubiCasa) {
    return <MSpinner />;
  }

  if (cubiCasa) {
    return (
      <>
        <div>
          {canQuickEdit && (
            <Button
              id={"quickEdit"}
              disabled={!quickEdit}
              onClick={() => window.open(quickEdit, "_blank").focus()}
              style={{ marginRight: "15px" }}
            >
              Quick Edit
            </Button>
          )}
          {canAdvancedEdit && (
            <Button
              id={"advancedEdit"}
              disabled={!advancedEdit}
              onClick={() => window.open(advancedEdit, "_blank").focus()}
              style={{ marginRight: "15px" }}
            >
              Advanced Edit
            </Button>
          )}
          {canFixRequest && (
            <Button
              id={"fixRequest"}
              disabled={showFixRequest}
              onClick={() => setShowFixRequest(true)}
              style={{ marginRight: "15px" }}
            >
              Fix Request
            </Button>
          )}
        </div>
        <br />
        <Grid container spacing={1} className="case-header">{cubiCasa}</Grid>
        <MDialog
          id={"fix-request-dialog"}
          title={"CubiCasa Fix Request"}
          visible={showFixRequest}
          onHide={() => setShowFixRequest(false)}
          focusOnMount={false}
          width={600}
        >
          {submittingFixRequest ? (
            <MSpinner />
          ) : (
            <>
              <div>Please describe the fix being requested to assist CubiCasa</div>
              <TextField
                id={"fix-request-text"}
                variant="standard"
                multiline
                rows={6}
                label={"Describe Fix Request:"}
                type={"text"}
                value={fixRequestText}
                onChange={(e) => setFixRequestText(e.target.value)}
              />
              <div>
                <Button onClick={submit}>Submit</Button>
                <Button variant="text" color="secondary" onClick={() => setShowFixRequest(false)}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </MDialog>
      </>
    );
  }

  return <div />;
};

CubiCasaSketch.propTypes = {
  shouldDisplay: PropTypes.func,
  controlNumber: PropTypes.number,
  makeErrorToast: PropTypes.func.isRequired,
  makeSuccessToast: PropTypes.func.isRequired,
  canQuickEdit: PropTypes.bool,
  canFixRequest: PropTypes.bool,
  canAdvancedEdit: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    canQuickEdit: userHasRight(CUBICASA_QUICKEDIT)(state),
    canAdvancedEdit: userHasRight(CUBICASA_ADVANCEDEDIT)(state),
    canFixRequest: userHasRight(CUBICASA_FIX_REQUEST)(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  makeErrorToast(text) {
    responseHandler.customToast(text, "error", dispatch);
  },
  makeSuccessToast(text) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CubiCasaSketch);
